// src/features/admin/components/AssessmentResultForm.jsx
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { X, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { BASE_URL } from "shared/lib/config";

function getStore() {
  if (sessionStorage.getItem("idToken")) return sessionStorage;
  if (localStorage.getItem("idToken")) return localStorage;
  return localStorage;
}

function normalizeSpaces(value) {
  return String(value ?? "").trim().replace(/\s+/g, " ");
}

function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/** =========================
 * DOB helpers
 * =========================
 * - UI input format: DD/MM/YYYY
 * - Backend will store: tanggal_lahir (Date) + compute age server-side
 */
function parseDobDDMMYYYY(input) {
  const v = String(input || "").trim();
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return { ok: false, date: null, reason: "Format harus DD/MM/YYYY" };

  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);

  if (mm < 1 || mm > 12) return { ok: false, date: null, reason: "Bulan tidak valid" };
  if (dd < 1 || dd > 31) return { ok: false, date: null, reason: "Tanggal tidak valid" };
  if (yyyy < 1900 || yyyy > 2100) return { ok: false, date: null, reason: "Tahun tidak valid" };

  const d = new Date(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) {
    return { ok: false, date: null, reason: "Tanggal tidak valid" };
  }

  // prevent future DOB
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (d > today) return { ok: false, date: null, reason: "Tanggal lahir tidak boleh di masa depan" };

  return { ok: true, date: d, reason: "" };
}
function unwrapMongoDate(v) {
  if (!v) return v;

  // Mongo Extended JSON: { $date: "..." } or { $date: { $numberLong: "..." } }
  if (typeof v === "object" && v.$date) {
    const d = v.$date;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && d.$numberLong) return Number(d.$numberLong);
  }

  return v;
}

function formatDobToDDMMYYYY(dateLike) {
  const raw = unwrapMongoDate(dateLike);
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";

  // ✅ use LOCAL getters (matches table rendering)
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());

  return `${dd}/${mm}/${yyyy}`;
}

function toDobInputValue(dateLike) {
  const raw = unwrapMongoDate(dateLike);
  if (!raw) return "";

  if (typeof raw === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(raw.trim())) return raw.trim();
  return formatDobToDDMMYYYY(raw);
}

/** =========================
 * Scoring
 * ========================= */
const SCALE_MAX = 130;
const THRESHOLDS = {
  developingMax: 89,
  averageMax: 110,
  aboveMax: 120,
  excellentMax: 130,
};

const clampScore = (score) => Math.max(0, Math.min(Number(score) || 0, SCALE_MAX));

function bandLabelFromScore(score) {
  const s = clampScore(score);
  if (s > THRESHOLDS.aboveMax) return "Unggul";
  if (s >= THRESHOLDS.averageMax + 1) return "Di atas Rata-rata";
  if (s >= THRESHOLDS.developingMax + 1) return "Rata-rata";
  return "Sedang Berkembang";
}

const METRIC_OPTIONS = [
  { key: "Vocabulary", label: "Vocabulary (GC)" },
  { key: "Number Sequence", label: "Number Sequence (GF)" },
  { key: "Narration Recall", label: "Narration Recall (GL)" },
  { key: "Complex Working Memory", label: "Complex Working Memory (GMW)" },
  { key: "Semantic Fluency", label: "Semantic Fluency (GR)" },
  { key: "Memory of Sound Pattern", label: "Memory of Sound Pattern (GA)" },
  { key: "Visualization", label: "Visualization (GV)" },
  { key: "Rate of Test Taking", label: "Rate of Test Taking (GS)" },
];

function normMetric(s) {
  return String(s || "").trim().toLowerCase().replace(/[\s_\-()]/g, "");
}

function buildInitialScores(result) {
  const base = METRIC_OPTIONS.map((m) => ({
    metric: m.key,
    metricLabel: m.label,
    score: "",
    band: "",
    recommendation: "",
    _error: "",
  }));

  if (!result || !Array.isArray(result.scores)) return base;

  return base.map((item) => {
    const targetKey = normMetric(item.metric);
    const targetLabel = normMetric(item.metricLabel);

    const existing =
      result.scores.find((s) => {
        const sm = normMetric(s?.metric);
        return sm === targetKey || sm === targetLabel;
      }) || null;

    if (!existing) return item;

    return {
      ...item,
      score: existing.score === null || existing.score === undefined ? "" : existing.score,
      band: existing.band || "",
      recommendation: existing.recommendation || "",
      _error: "",
    };
  });
}

async function fetchJson(url, token, options = {}) {
  const res = await fetch(url, {
    method: options.method || "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
    body: options.body,
  });

  const text = await res.text();
  if (!res.ok) {
    let body = {};
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = {};
    }
    throw new Error(body?.message || `Request failed (${res.status})`);
  }

  return text ? JSON.parse(text) : {};
}

function extractArrayFromResponse(data, keys = []) {
  for (const k of keys) {
    if (Array.isArray(data?.[k])) return data[k];
    if (Array.isArray(data?.data?.[k])) return data.data[k];
    if (Array.isArray(data?.result?.[k])) return data.result[k];
  }
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
}

const MIN_QUERY_LEN = 2;
const MAX_SUGGESTIONS = 8;

// ---- helpers: robust id/name extractor ----
function pickId(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v?._id || v?.id || "";
}
function pickName(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v?.name || "";
}
function meansSame(a, b) {
  return normalizeSpaces(a).toLowerCase() === normalizeSpaces(b).toLowerCase();
}

// ✅ strict positive integer (>=1) score normalization
function normalizePositiveIntScore(raw) {
  const s = String(raw ?? "").trim();
  if (s === "") return ""; // allow empty while typing; submit will validate

  if (!/^\d+$/.test(s)) return "__INVALID__";

  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return "__INVALID__";
  if (n < 1) return 1;

  return n;
}

function validateScoresStrict(scores) {
  const issues = [];
  (scores || []).forEach((s) => {
    const label = s.metricLabel || s.metric || "Skor";
    const raw = s.score;

    if (raw === "" || raw === null || raw === undefined) {
      issues.push({ label, message: "wajib diisi" });
      return;
    }

    const str = String(raw).trim();
    if (!/^\d+$/.test(str)) {
      issues.push({ label, message: "harus angka bulat positif (tanpa huruf/koma/titik)" });
      return;
    }

    const n = Number(str);
    if (!Number.isInteger(n) || n < 1) {
      issues.push({ label, message: "minimal 1" });
      return;
    }
  });

  return issues;
}

function friendlyApiError(errBody) {
  const msg = String(errBody?.message || "").trim();
  if (!msg) return "Gagal menyimpan hasil asesmen.";

  if (msg.toLowerCase().includes("validation failed")) {
    return "Ada field yang belum valid. Pastikan semua skor terisi angka bulat positif (minimal 1).";
  }

  if (msg.length > 160) return msg.slice(0, 160) + "...";
  return msg;
}

export default function AssessmentResultForm({ result, onClose, onSaved }) {
  const isEdit = Boolean(result?.id || result?._id);
  const id = result?.id || result?._id || null;

  // ✅ Tahun Ajaran autocomplete state
  const [ayOptions, setAyOptions] = useState([]); // [{_id, name}]
  const [ayLoading, setAyLoading] = useState(false);
  const [ayFocused, setAyFocused] = useState(false);
  const ayInputRef = useRef(null);

  // Distributors / vouchers / schools
  const [distributors, setDistributors] = useState([]); // [{_id, name}]
  const [distributorsLoading, setDistributorsLoading] = useState(false);

  const [voucherOptions, setVoucherOptions] = useState([]); // [{code, label}]
  const [voucherLoading, setVoucherLoading] = useState(false);

  const [schoolOptions, setSchoolOptions] = useState([]); // [{_id,name}]
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [schoolFocused, setSchoolFocused] = useState(false);
  const schoolInputRef = useRef(null);

  // Distributor dropdown open state (custom dropdown)
  const [distOpen, setDistOpen] = useState(false);
  const distWrapRef = useRef(null);

  // Score inputs refs for "Enter -> next"
  const scoreInputRefs = useRef([]);

  const initialDistributorId =
    pickId(result?.distributor_id) ||
    pickId(result?.distributorId) ||
    pickId(result?.distributor?.schoolId) ||
    pickId(result?.distributor?.id) ||
    pickId(result?.distributor?._id) ||
    "";

  const initialDistributorName =
    normalizeSpaces(
      pickName(result?.distributor_name) ||
        pickName(result?.distributorName) ||
        pickName(result?.distributor) ||
        pickName(result?.distributor?.name) ||
        ""
    ) || "";

  // ✅ DOB: UI uses formData.dob (DD/MM/YYYY). Backend stores tanggal_lahir Date.
  const initialDob =
    result?.dob ||
    result?.dateOfBirth ||
    result?.tanggal_lahir ||
    result?.tanggalLahir ||
    "";

  const [formData, setFormData] = useState({
    student_id: result?.student_id || result?.studentId || "",
    test_taker_name: result?.test_taker_name || result?.testTakerName || "",
    gender: result?.gender || "",

    // DD/MM/YYYY
    dob: toDobInputValue(initialDob),

    voucher: result?.voucher || "",

    distributor_id: initialDistributorId || "",
    distributor_name: initialDistributorName || "",

    school_name: result?.school_name || result?.schoolName || result?.school?.name || "",
    school_id: result?.school_id || result?.schoolId || result?.school?.id || result?.school?._id || "",

    // ✅ Tahun Ajaran:
    // - academic_year_name is typed by user (e.g. "2024/2025")
    // - academic_year_id filled when user selects suggestion (optional)
    academic_year_id: result?.academic_year_id || result?.academicYearId || "",
    academic_year_name: result?.academic_year_name || result?.academicYearName || "",

    class_id: result?.class_id || result?.classId || "",
    class_name: result?.class_name || result?.className || "",

    teacher_id: result?.teacher_id || result?.teacherId || "",

    strengths: result?.strengths || "",
    learning_styles: result?.learning_styles || result?.learningStyles || "",
    extracurricular_recommendations:
      result?.extracurricular_recommendations || result?.extracurricularRecommendations || "",
    minat_bakat: result?.minat_bakat || result?.MinatBakat || "",

    scores: buildInitialScores(result),
  });

  const [loading, setLoading] = useState(false);

  const token = useMemo(() => {
    const store = getStore();
    return store.getItem("idToken");
  }, []);

  const dobParsed = useMemo(() => parseDobDDMMYYYY(formData.dob), [formData.dob]);

  /** =========================
   * Close distributor dropdown on outside click
   * ========================= */
  useEffect(() => {
    const onDocClick = (e) => {
      if (!distOpen) return;
      const el = distWrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setDistOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [distOpen]);

  // -------------------------
  // Load distributors list
  // -------------------------
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token) return;

      try {
        setDistributorsLoading(true);
        const data = await fetchJson(`${BASE_URL}/api/admin/schools?limit=500`, token);

        const rows = extractArrayFromResponse(data, ["schools"])
          .map((s) => ({
            _id: s?._id || s?.id || "",
            name: s?.name || "",
          }))
          .filter((s) => s._id && s.name);

        if (!cancelled) setDistributors(rows);
      } catch (e) {
        if (!cancelled) setDistributors([]);
        console.error("[AssessmentResultForm] load distributors failed:", e);
      } finally {
        if (!cancelled) setDistributorsLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // When editing, if distributor_id exists but name is missing, fill distributor_name once distributors list is loaded.
  useEffect(() => {
    if (!formData.distributor_id) return;
    const found = (distributors || []).find((d) => String(d._id) === String(formData.distributor_id));
    if (found?.name && !meansSame(found.name, formData.distributor_name)) {
      setFormData((prev) => ({ ...prev, distributor_name: found.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributors, formData.distributor_id]);

  // -------------------------
  // Default distributor to current user's schoolId (create only)
  // -------------------------
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token) return;
      if (isEdit) return;
      if (formData.distributor_id) return;

      const candidates = [`${BASE_URL}/api/admin/me`, `${BASE_URL}/api/me`, `${BASE_URL}/api/auth/me`];

      for (const url of candidates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const me = await fetchJson(url, token);
          const user = me?.user || me?.data?.user || me?.data || me?.result?.user || me?.result || null;

          const school = user?.school || user?.schoolId || user?.distributor || user?.distributorId || null;

          const schoolId = pickId(school);
          const schoolName = pickName(school) || user?.schoolName || "";

          if (schoolId) {
            if (cancelled) return;
            setFormData((prev) => ({
              ...prev,
              distributor_id: String(schoolId),
              distributor_name: String(schoolName || ""),
            }));
            return;
          }
        } catch {
          // ignore
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [token, isEdit, formData.distributor_id]);

  // -------------------------
  // Sekolah suggestions (optional)
  // -------------------------
  const schoolQueryRaw = useMemo(() => String(formData.school_name || "").trim(), [formData.school_name]);
  const schoolQuery = useDebouncedValue(schoolQueryRaw, 250);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const q = normalizeSpaces(schoolQuery);
      if (!token) return;

      if (!q || q.length < MIN_QUERY_LEN) {
        setSchoolOptions([]);
        return;
      }

      try {
        setSchoolLoading(true);

        const data = await fetchJson(`${BASE_URL}/api/admin/schools?q=${encodeURIComponent(q)}`, token);

        const rows = extractArrayFromResponse(data, ["schools"])
          .map((s) => ({
            _id: s?._id || s?.id || "",
            name: s?.name || "",
          }))
          .filter((s) => s._id && s.name);

        const needle = normalizeSpaces(q).toLowerCase();
        const filtered = rows
          .filter((s) => normalizeSpaces(s.name).toLowerCase().includes(needle))
          .slice(0, MAX_SUGGESTIONS);

        if (!cancelled) setSchoolOptions(filtered);
      } catch (e) {
        if (!cancelled) setSchoolOptions([]);
        console.error("[AssessmentResultForm] fetch sekolah failed:", e);
      } finally {
        if (!cancelled) setSchoolLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [schoolQuery, token]);

  const schoolExactMatch = useMemo(() => {
    const typed = normalizeSpaces(formData.school_name).toLowerCase();
    if (!typed) return false;

    return (schoolOptions || []).some((s) => normalizeSpaces(s?.name).toLowerCase() === typed);
  }, [formData.school_name, schoolOptions]);

  const showSchoolSuggestions =
    schoolFocused && schoolQueryRaw.trim().length >= MIN_QUERY_LEN && (schoolLoading || schoolOptions.length > 0);

  const selectSchool = useCallback((school) => {
    setFormData((prev) => ({
      ...prev,
      school_name: school?.name || "",
      school_id: school?._id || "",
    }));
    setSchoolFocused(false);
    requestAnimationFrame(() => schoolInputRef.current?.focus());
  }, []);

  const handleSchoolChange = (value) => {
    const v = String(value || "");
    const matched = (schoolOptions || []).find(
      (s) => normalizeSpaces(s?.name).toLowerCase() === normalizeSpaces(v).toLowerCase()
    );

    setFormData((prev) => ({
      ...prev,
      school_name: v,
      school_id: matched ? matched._id : "",
    }));
  };

  // -------------------------
  // Tahun Ajaran suggestions (NEW)
  // -------------------------
  const ayQueryRaw = useMemo(() => String(formData.academic_year_name || "").trim(), [formData.academic_year_name]);
  const ayQuery = useDebouncedValue(ayQueryRaw, 250);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const q = normalizeSpaces(ayQuery);
      if (!token) return;

      if (!q || q.length < MIN_QUERY_LEN) {
        setAyOptions([]);
        return;
      }

      try {
        setAyLoading(true);

        // This endpoint should return academic years list filtered by q.
        // If you don't have it yet, backend can just ignore q and return all (then filter client-side).
       const data = await fetchJson(`${BASE_URL}/api/admin/settings/academic-years?q=${encodeURIComponent(q)}`, token);


        const rows = extractArrayFromResponse(data, ["data", "academicYears", "academic_years", "rows"])
          .map((ay) => ({
            _id: ay?._id || ay?.id || "",
            name: ay?.name || "",
          }))
          .filter((x) => x._id && x.name);

        const needle = normalizeSpaces(q).toLowerCase();
        const filtered = rows
          .filter((x) => normalizeSpaces(x.name).toLowerCase().includes(needle))
          .slice(0, MAX_SUGGESTIONS);

        if (!cancelled) setAyOptions(filtered);
      } catch (e) {
        if (!cancelled) setAyOptions([]);
        console.error("[AssessmentResultForm] fetch academic years failed:", e);
      } finally {
        if (!cancelled) setAyLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [ayQuery, token]);

  const ayExactMatch = useMemo(() => {
    const typed = normalizeSpaces(formData.academic_year_name).toLowerCase();
    if (!typed) return false;

    return (ayOptions || []).some((x) => normalizeSpaces(x?.name).toLowerCase() === typed);
  }, [formData.academic_year_name, ayOptions]);

  const showAySuggestions =
    ayFocused && ayQueryRaw.trim().length >= MIN_QUERY_LEN && (ayLoading || ayOptions.length > 0);

  const selectAy = useCallback((ay) => {
    setFormData((prev) => ({
      ...prev,
      academic_year_id: ay?._id || "",
      academic_year_name: ay?.name || "",
    }));
    setAyFocused(false);
    requestAnimationFrame(() => ayInputRef.current?.focus());
  }, []);

  const handleAyChange = (value) => {
    const v = String(value || "");
    const matched = (ayOptions || []).find(
      (x) => normalizeSpaces(x?.name).toLowerCase() === normalizeSpaces(v).toLowerCase()
    );

    setFormData((prev) => ({
      ...prev,
      academic_year_name: v,
      academic_year_id: matched ? matched._id : "", // clear until selected / matches
    }));
  };

  // -------------------------
  // Voucher dropdown (unused vouchers by distributor)
  // -------------------------
  useEffect(() => {
    let cancelled = false;

    async function run() {
      const distributorId = formData.distributor_id;
      if (!token) return;

      if (!distributorId) {
        setVoucherOptions([]);
        if (!isEdit) setFormData((p) => ({ ...p, voucher: "" }));
        return;
      }

      try {
        setVoucherLoading(true);

        let rows = [];

        try {
          const data = await fetchJson(
            `${BASE_URL}/api/admin/schools/${encodeURIComponent(distributorId)}/vouchers?status=unused`,
            token
          );

          rows = extractArrayFromResponse(data, ["vouchers"]).map((v) => ({
            code: String(v?.code || v?.voucher || "").trim(),
            status: v?.status || "",
            usedByAssessmentResultId: v?.usedByAssessmentResultId || null,
          }));
        } catch {
          const data = await fetchJson(`${BASE_URL}/api/admin/schools/${encodeURIComponent(distributorId)}`, token);

          const school = data?.school || data?.data?.school || data?.data || data?.result?.school || data?.result || {};

          const pool = Array.isArray(school?.vouchers) ? school.vouchers : [];
          rows = pool.map((v) => ({
            code: String(v?.code || "").trim(),
            status: v?.status || "",
            usedByAssessmentResultId: v?.usedByAssessmentResultId || null,
          }));
        }

        const unused = rows
          .filter((v) => v.code)
          .filter((v) => {
            const status = String(v.status || "").toLowerCase();
            return status !== "used" && !v.usedByAssessmentResultId;
          })
          .map((v) => ({
            code: v.code,
            label: v.code,
          }))
          .sort((a, b) => a.code.localeCompare(b.code));

        if (cancelled) return;

        setVoucherOptions(unused);

        if (!isEdit) {
          if (unused.length === 0) {
            setFormData((p) => ({ ...p, voucher: "" }));
          } else if (unused.length === 1) {
            setFormData((p) => ({ ...p, voucher: unused[0].code }));
          } else {
            const current = normalizeSpaces(formData.voucher);
            const stillOk = unused.some((x) => x.code === current);
            if (!stillOk) setFormData((p) => ({ ...p, voucher: "" }));
          }
        }
      } catch (e) {
        if (cancelled) return;
        setVoucherOptions([]);
        if (!isEdit) setFormData((p) => ({ ...p, voucher: "" }));
        console.error("[AssessmentResultForm] load vouchers failed:", e);
      } finally {
        if (!cancelled) setVoucherLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.distributor_id, token, isEdit]);

  // -------------------------
  // Scores (digits only, min 1)
  // -------------------------
  const handleScoreChange = (index, value) => {
    const normalized = normalizePositiveIntScore(value);

    setFormData((prev) => ({
      ...prev,
      scores: (prev.scores || []).map((s, i) =>
        i === index
          ? {
              ...s,
              score: normalized === "__INVALID__" ? s.score : normalized,
              _error:
                normalized === "__INVALID__"
                  ? "Hanya angka bulat positif (tanpa koma/titik/huruf)."
                  : "",
            }
          : s
      ),
    }));
  };

  const blockNonIntegerKeys = (e) => {
    const allowed = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    if (allowed.includes(e.key)) return;

    if (e.ctrlKey || e.metaKey) return;

    if (!/^\d$/.test(e.key)) e.preventDefault();
  };

  const focusNextScore = (index) => {
    const next = scoreInputRefs.current?.[index + 1];
    if (next && typeof next.focus === "function") next.focus();
  };

  // -------------------------
  // DOB input helper (auto insert slashes)
  // -------------------------
  const handleDobChange = (raw) => {
    let v = String(raw || "").replace(/[^\d]/g, "");
    if (v.length > 8) v = v.slice(0, 8);

    let out = v;
    if (v.length >= 3) out = `${v.slice(0, 2)}/${v.slice(2)}`;
    if (v.length >= 5) out = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`;

    setFormData((p) => ({ ...p, dob: out }));
  };

  // -------------------------
  // Submit
  // -------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const distributorId = String(formData.distributor_id || "").trim();
    if (!distributorId) {
      toast.error("Distributor wajib dipilih.");
      return;
    }

    // ✅ validate DOB; backend stores tanggal_lahir and computes age
    if (!dobParsed.ok) {
      toast.error(`Tanggal lahir tidak valid. ${dobParsed.reason}`);
      return;
    }

    // ✅ validate Tahun Ajaran by NAME (typed)
    const academicYearName = normalizeSpaces(formData.academic_year_name);
    if (!academicYearName) {
      toast.error("Tahun Ajaran wajib diisi (contoh: 2024/2025).");
      return;
    }

    // ✅ validate scores
    const scoreIssues = validateScoresStrict(formData.scores);
    if (scoreIssues.length) {
      const first = scoreIssues[0];
      setFormData((prev) => ({
        ...prev,
        scores: (prev.scores || []).map((s) => {
          const label = s.metricLabel || s.metric || "Skor";
          if (label === first.label) return { ...s, _error: first.message };
          return s;
        }),
      }));
      toast.error(`Skor tidak valid: ${first.label} — ${first.message}`);
      return;
    } else {
      setFormData((prev) => ({
        ...prev,
        scores: (prev.scores || []).map((s) => ({ ...s, _error: "" })),
      }));
    }

    setLoading(true);
    const toastId = toast.loading(isEdit ? "Menyimpan perubahan..." : "Menyimpan data...");

    try {
      const idToken = token;
      if (!idToken) {
        toast.update(toastId, {
          render: "Token login tidak ditemukan. Silakan login ulang.",
          type: "error",
          isLoading: false,
          autoClose: 3500,
        });
        setLoading(false);
        return;
      }

      const distributorName = (() => {
        const found = distributors.find((d) => String(d._id) === distributorId);
        return found?.name || formData.distributor_name || "";
      })();

      const payload = {
        student_id: formData.student_id || null,
        test_taker_name: normalizeSpaces(formData.test_taker_name),
        gender: formData.gender || null,

        // ✅ IMPORTANT: send tanggal_lahir (DOB) only; NOT age
        // Backend should parse/convert and save into AssessmentResult.tanggal_lahir
        tanggal_lahir: dobParsed.date ? dobParsed.date.toISOString() : null,

        voucher: normalizeSpaces(formData.voucher),

        distributor_id: distributorId,
        distributor: distributorName || null,

        school_id: formData.school_id || null,
        school_name: normalizeSpaces(formData.school_name) || "",

        // ✅ Tahun Ajaran: send name always; id optional if selected
        academic_year_id: formData.academic_year_id || null,
        academic_year_name: academicYearName,

        class_id: formData.class_id || null,
        class_name: normalizeSpaces(formData.class_name) || "",

        teacher_id: formData.teacher_id || null,

        strengths: formData.strengths || "",
        learning_styles: formData.learning_styles || "",
        extracurricular_recommendations: formData.extracurricular_recommendations || "",
        minat_bakat: formData.minat_bakat || "",

        scores: Array.isArray(formData.scores)
          ? formData.scores.map((s) => {
              const scoreNum = s.score === "" || s.score === null ? null : Number(s.score);
              return {
                metric: s.metric,
                score: scoreNum,
                band: scoreNum === null ? "" : bandLabelFromScore(scoreNum),
                recommendation: s.recommendation || "",
              };
            })
          : [],
      };

      if (!isEdit) {
        if (!payload.voucher) {
          toast.update(toastId, {
            render:
              voucherOptions.length === 0
                ? "Tidak ada voucher tersedia untuk distributor ini."
                : "Voucher wajib dipilih.",
            type: "error",
            isLoading: false,
            autoClose: 4000,
          });
          setLoading(false);
          return;
        }
      }

      const method = isEdit ? "PATCH" : "POST";
      const url = isEdit ? `${BASE_URL}/api/admin/assessments/${id}` : `${BASE_URL}/api/admin/assessments`;

      const resp = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      const raw = await resp.text();

      if (!resp.ok) {
        let errBody = {};
        try {
          errBody = raw ? JSON.parse(raw) : {};
        } catch {
          errBody = {};
        }

        toast.update(toastId, {
          render: friendlyApiError(errBody),
          type: "error",
          isLoading: false,
          autoClose: 4000,
        });

        setLoading(false);
        return;
      }

      const data = raw ? JSON.parse(raw) : {};
      toast.update(toastId, {
        render: isEdit ? "Hasil asesmen berhasil diperbarui." : "Hasil asesmen berhasil ditambahkan.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });

      onSaved?.(data);
      setLoading(false);
      onClose?.();
    } catch (err) {
      console.error("handleSubmit assessment error:", err);
      toast.update(toastId, {
        render: err?.message || "Terjadi kesalahan saat menyimpan. Coba lagi nanti.",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
      setLoading(false);
    }
  };

  const distributorSelectValue = String(formData.distributor_id || "");
  const distributorSelectedName =
    formData.distributor_name || distributors.find((d) => String(d._id) === String(distributorSelectValue))?.name || "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-gray-900">{isEdit ? "Edit Hasil Asesmen" : "Tambah Hasil Asesmen"}</h3>
          <button onClick={onClose} type="button" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nama Peserta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nama Peserta <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.test_taker_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    test_taker_name: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData((prev) => ({ ...prev, gender: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Pilih gender</option>
                <option value="Laki-laki">Laki-laki</option>
                <option value="Perempuan">Perempuan</option>
              </select>
            </div>

            {/* Tanggal Lahir */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tanggal Lahir <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.dob}
                onChange={(e) => handleDobChange(e.target.value)}
                placeholder="DD/MM/YYYY (contoh: 21/08/2010)"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formData.dob && !dobParsed.ok ? "border-red-500" : "border-gray-300"
                }`}
              />
              {formData.dob && !dobParsed.ok ? (
                <p className="text-xs text-red-600 mt-1">{dobParsed.reason}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">Format wajib: DD/MM/YYYY</p>
              )}
            </div>

            {/* Tahun Ajaran (autocomplete + allow create) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tahun Ajaran <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <input
                  ref={ayInputRef}
                  type="text"
                  value={formData.academic_year_name || ""}
                  onChange={(e) => handleAyChange(e.target.value)}
                  onFocus={() => setAyFocused(true)}
                  onBlur={() => setTimeout(() => setAyFocused(false), 120)}
                  placeholder="Contoh: 2024/2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoComplete="off"
                  required
                />

                {showAySuggestions && (
                  <div className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                    {!ayExactMatch && ayQueryRaw.trim() && !ayLoading && (
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          // use typed value; backend can create if not exists
                          setFormData((prev) => ({
                            ...prev,
                            academic_year_name: ayQueryRaw.trim(),
                            academic_year_id: "",
                          }));
                          setAyFocused(false);
                          requestAnimationFrame(() => ayInputRef.current?.focus());
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-blue-700 hover:bg-blue-50 transition-colors border-b border-gray-100"
                        title="Gunakan tahun ajaran ini (akan dibuat jika belum ada)"
                      >
                        Gunakan: <span className="font-semibold">{ayQueryRaw.trim()}</span>
                      </button>
                    )}

                    {ayLoading ? (
                      <div className="px-4 py-2.5 text-sm text-gray-500">Mencari...</div>
                    ) : ayOptions.length === 0 ? (
                      <div className="px-4 py-2.5 text-sm text-gray-500">Tidak ada saran</div>
                    ) : (
                      ayOptions.map((ay) => (
                        <button
                          key={ay._id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectAy(ay);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 transition-colors"
                        >
                          {ay.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-1">
                Ketik untuk mencari. Kalau belum ada, backend bisa membuat Tahun Ajaran baru dari teks ini.
              </p>
            </div>

            {/* Distributor */}
            <div ref={distWrapRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Distributor <span className="text-red-500">*</span>
              </label>

              <button
                type="button"
                disabled={distributorsLoading}
                onClick={() => setDistOpen((v) => !v)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <span className={distributorSelectValue ? "text-gray-900" : "text-gray-500"}>
                  {distributorsLoading
                    ? "Memuat distributor..."
                    : distributorSelectValue
                    ? distributorSelectedName
                    : "Pilih distributor"}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>

              {distOpen && !distributorsLoading && (
                <div className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          distributor_id: "",
                          distributor_name: "",
                        }));
                        setVoucherOptions([]);
                        if (!isEdit) setFormData((p) => ({ ...p, voucher: "" }));
                        setDistOpen(false);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors border-b border-gray-100"
                    >
                      Pilih distributor
                    </button>

                    {(distributors || []).map((d) => (
                      <button
                        key={d._id}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            distributor_id: String(d._id),
                            distributor_name: d.name || "",
                          }));
                          setDistOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          String(d._id) === distributorSelectValue
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-800 hover:bg-gray-50"
                        }`}
                      >
                        {d.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Voucher */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher <span className="text-red-500">*</span>
              </label>

              {isEdit ? (
                <input
                  type="text"
                  readOnly
                  value={formData.voucher || ""}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                />
              ) : (
                <select
                  required
                  value={formData.voucher || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, voucher: e.target.value }))}
                  disabled={!formData.distributor_id || voucherLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                >
                  {!formData.distributor_id ? (
                    <option value="">Pilih distributor terlebih dahulu</option>
                  ) : voucherLoading ? (
                    <option value="">Memuat voucher...</option>
                  ) : voucherOptions.length === 0 ? (
                    <option value="">Tidak ada voucher tersedia (unused)</option>
                  ) : (
                    <>
                      <option value="">Pilih voucher</option>
                      {voucherOptions.map((v) => (
                        <option key={v.code} value={v.code}>
                          {v.label}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              )}

              {!isEdit && formData.distributor_id && !voucherLoading && voucherOptions.length === 0 ? (
                <p className="text-xs text-amber-700 mt-1">
                  Distributor ini tidak memiliki voucher unused. Tambahkan voucher ke pool distributor atau pilih
                  distributor lain.
                </p>
              ) : null}
            </div>

            {/* Sekolah */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sekolah (opsional)</label>

              <div className="relative">
                <input
                  ref={schoolInputRef}
                  type="text"
                  value={formData.school_name || ""}
                  onChange={(e) => handleSchoolChange(e.target.value)}
                  onFocus={() => setSchoolFocused(true)}
                  onBlur={() => setTimeout(() => setSchoolFocused(false), 120)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ketik min. 2 huruf untuk mencari..."
                  autoComplete="off"
                />

                {showSchoolSuggestions && (
                  <div className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                    {!schoolExactMatch && schoolQueryRaw.trim() && !schoolLoading && (
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setFormData((prev) => ({
                            ...prev,
                            school_name: schoolQueryRaw.trim(),
                            school_id: "",
                          }));
                          setSchoolFocused(false);
                          requestAnimationFrame(() => schoolInputRef.current?.focus());
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-blue-700 hover:bg-blue-50 transition-colors border-b border-gray-100"
                        title="Gunakan nama ini (akan dibuat jika belum ada)"
                      >
                        Gunakan: <span className="font-semibold">{schoolQueryRaw.trim()}</span>
                      </button>
                    )}

                    {schoolLoading ? (
                      <div className="px-4 py-2.5 text-sm text-gray-500">Mencari...</div>
                    ) : schoolOptions.length === 0 ? (
                      <div className="px-4 py-2.5 text-sm text-gray-500">Tidak ada saran</div>
                    ) : (
                      schoolOptions.map((school) => (
                        <button
                          key={school._id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectSchool(school);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 transition-colors"
                        >
                          {school.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Kelas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Kelas</label>
              <input
                type="text"
                placeholder="Tulis nama kelas (opsional)"
                value={formData.class_name || ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, class_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Scores */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Skor Kemampuan (8 Kemampuan)</h4>

            <div className="space-y-3">
              {(formData.scores || []).map((s, index) => {
                const bandLabel =
                  s.score === null || s.score === "" || Number.isNaN(Number(s.score))
                    ? ""
                    : bandLabelFromScore(s.score);

                return (
                  <div key={s.metric || index} className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <div className="text-sm font-semibold text-gray-800">{s.metricLabel || s.metric}</div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-600 mb-1">Skor</label>
                        <input
                          ref={(el) => {
                            scoreInputRefs.current[index] = el;
                          }}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={s.score ?? ""}
                          onKeyDown={(e) => {
                            blockNonIntegerKeys(e);
                            if (e.key === "Enter") {
                              e.preventDefault();
                              focusNextScore(index);
                            }
                          }}
                          onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");
                            if (!/^\d+$/.test(String(pasted || "").trim())) e.preventDefault();
                          }}
                          onChange={(e) => handleScoreChange(index, e.target.value)}
                          className={`w-full px-2.5 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            s._error ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="Min 1"
                        />
                        {s._error ? <p className="text-xs text-red-600 mt-1">{s._error}</p> : null}
                      </div>

                      <div className="col-span-2">
                        <label className="block text-[11px] font-medium text-gray-600 mb-1">Band (otomatis)</label>
                        <input
                          type="text"
                          value={bandLabel}
                          readOnly
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm bg-gray-50 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Menyimpan..." : isEdit ? "Perbarui" : "Tambah"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Batal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
